import {observer} from "mobx-react-lite";
import React from "react";
import {Col, Collapse, Form, Row} from "reactstrap";
import {useStores} from "../../../../stores/useStores";
import DateEdit from "../../../../components/organisms/dateEdit/dateEdit";
import Selection from "../../../../components/organisms/selection/selection";
import MultiVillageSelect from "../../../../components/molecules/multiVillageSelect/multiVillageSelect";
import {MultiVillageSelectObservableList} from "../../../../stores/observables/multiVillageSelectObservableList";
import {
  allVillageKeyValue,
  allVillagesInAU,
  allVillagesInNZ
} from "../../../../shared/constants";
import {KeyValue} from "../../../../stores/observables/keyValue";
import {ObservableList} from "../../../../stores/observables/observableList";
import "./metricsSearchOptions.sass";
import {IconSource} from "../../../../shared/enums/iconSource";
import Banner from "../../../../components/atoms/banner/banner";
import {MetricsTabOption} from "../../../../utils/metrics/metricTypes";

export interface IMetricsSearchOptionsProps {
  selectedTab: MetricsTabOption;
}

const MetricsSearchOptions: React.FC<IMetricsSearchOptionsProps> = ({
  selectedTab
}) => {
  const {
    domainStore: {
      metricsDomainStore: {
        startDate,
        endDate,
        selectedVillages,
        setSelectedVillages,
        selectedCalendarType,
        setSelectedCalendarType,
        getVillages,
        requiresReload,
        isDirty
      }
    }
  } = useStores();

  const multivillages =
    getVillages()?.map((village) => new KeyValue(village.id, village.name)) ??
    [];

  const villages = new MultiVillageSelectObservableList(
    [allVillageKeyValue, allVillagesInNZ, allVillagesInAU, ...multivillages],
    selectedVillages,
    1,
    setSelectedVillages
  );
  villages.isDirty = isDirty;

  const handleCalendarTypeChanged = (selectedItem: KeyValue) => {
    const selectedCalendarType = (() => {
      switch (selectedItem?.value) {
        case "Enhanced":
          return "Enhanced";
        case "Engage":
          return "Engage";
        case "All":
          return "All";
      }
    })();

    setSelectedCalendarType(selectedCalendarType);
  };

  const calendarTypeOptions = new ObservableList<KeyValue>(
    [
      new KeyValue("Enhanced", "Enhanced"),
      new KeyValue("Engage", "Engage"),
      new KeyValue("All", "All")
    ],
    selectedCalendarType
  );

  const colSpan = selectedTab === "Events" ? 4 : 6;

  return (
    <div className={"metrics-search-options"}>
      <Form>
        <Row>
          <Col md={colSpan}>
            <DateEdit
              label={"From"}
              value={startDate}
              timeZone={"Pacific/Auckland"}
              dateFormat={"dd/MM/yyyy"}
              placeholder={"Start Date"}
              showTimeSelect={false}
              validationMessage="Start date must be before end date"
              overrideIsRequired={true}
              position={"bottom"}
            />
          </Col>
          <Col md={colSpan}>
            <DateEdit
              label={"To"}
              value={endDate}
              timeZone={"Pacific/Auckland"}
              dateFormat={"dd/MM/yyyy"}
              placeholder={"End Date"}
              showTimeSelect={false}
              validationMessage="End date must be after start date"
              overrideIsRequired={true}
              position={"bottom"}
            />
          </Col>

          {selectedTab === "Events" && (
            <Col md={colSpan}>
              <Selection
                label={"Calendar Type"}
                value={calendarTypeOptions}
                onChange={handleCalendarTypeChanged}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={12}>
            <MultiVillageSelect
              villages={villages}
              suppressAllVillageBanner={true}
              invalidMinSelectedVillageErrorMessage={
                "Please select at least 1 village"
              }
            />
          </Col>
        </Row>
      </Form>

      <Collapse isOpen={requiresReload}>
        <Row>
          <Col sm={12}>
            <Banner
              text={
                "Search parameters have changed. Please click search to reload data."
              }
              icon={IconSource.info}
            />
          </Col>
        </Row>
      </Collapse>
    </div>
  );
};

export default observer(MetricsSearchOptions);
